import { signal } from '@angular/core';
import type { DocumentNode } from 'graphql';
import { firstValueFrom } from 'rxjs';
import { buildSearchWhere, buildSortOrder, DEFAULT_PAGE_SIZE } from '../components/data-table';
import { useGraphqlPaginate } from '../composables/atoms/graphql/use-graphql-paginate';
import type { WhereClause } from '../query-dsl';

export interface UseAsyncSearchListConfig<T> {
  /** GraphQL list config of the target aggregate. */
  listConfig: {
    queryPagination: DocumentNode;
    paginationKey: string;
  };
  /** Searchable column ids — typically `getSearchableColumnIds(targetColumns)`. */
  searchColumns: string[];
  /** Initial rows seeded from the resolver (avoids extra fetch on first open). */
  initial?: T[];
  /** Page size for `paginate()` calls. Defaults to `DEFAULT_PAGE_SIZE`. */
  pageSize?: number;
  /** Filter applied to every request, ANDed with the search text: restricts what can be offered. */
  where?: WhereClause;
}

/**
 * Async, paginated, searchable option list for relational widgets.
 *
 * Encapsulates the state and side effects needed to drive a search-select
 * combobox against a target aggregate's GraphQL list-config:
 *
 * - Tracks current `query` and `offset`.
 * - Replaces options on `onSearch(q)` (resets offset to 0).
 * - Appends options on `onLoadMore()` (offset += pageSize).
 * - Stops firing when a fetch returns fewer rows than `pageSize` (`hasMore` heuristic).
 *
 * Designed for `<au-async-search-select>` / `<au-async-multiple-search-select>`
 * hosts. Sync variants don't need this — their options are fully preloaded.
 */
export function useAsyncSearchList<T extends { id: string }>(config: UseAsyncSearchListConfig<T>) {
  const pageSize = config.pageSize ?? DEFAULT_PAGE_SIZE;
  const initial = config.initial ?? [];

  const paginate = useGraphqlPaginate<T>(
    config.listConfig.queryPagination,
    config.listConfig.paginationKey,
  );

  const options = signal<T[]>(initial);
  const query = signal<string>('');
  const offset = signal<number>(0);
  // Heuristic: if the initial seed already filled a page, more may exist.
  const hasMore = signal<boolean>(initial.length >= pageSize);

  // WHY: responses can arrive out of order and `paginate.loading` is one flag shared by every
  // request. `generation` (bumped by each search) makes the latest search win and lets a stale
  // load-more be discarded; `loading` tracks only the CURRENT generation's request.
  let generation = 0;
  const loading = signal(false);

  async function fetchPage(off: number): Promise<T[]> {
    const search = query() ? buildSearchWhere(query(), config.searchColumns) : undefined;
    const where =
      config.where && search ? { and: [config.where, search] } : (config.where ?? search);
    const data = await firstValueFrom(
      paginate.paginate({
        query: { where, offset: off, limit: pageSize, order: buildSortOrder([]) },
      }),
    );
    return data.rows;
  }

  async function onSearch(q: string): Promise<void> {
    const current = ++generation;
    query.set(q);
    offset.set(0);
    hasMore.set(true);
    loading.set(true);
    try {
      const rows = await fetchPage(0);
      if (current !== generation) return;
      options.set(rows);
      hasMore.set(rows.length >= pageSize);
    } finally {
      if (current === generation) loading.set(false);
    }
  }

  async function onLoadMore(): Promise<void> {
    if (!hasMore()) return;
    if (loading()) return;
    const current = generation;
    const newOffset = offset() + pageSize;
    loading.set(true);
    try {
      const rows = await fetchPage(newOffset);
      if (current !== generation) return;
      offset.set(newOffset);
      // A seeded row (the record's linked value) can reappear on a later page.
      options.update((curr) => {
        const known = new Set(curr.map((option) => option.id));
        return [...curr, ...rows.filter((row) => !known.has(row.id))];
      });
      hasMore.set(rows.length >= pageSize);
    } finally {
      if (current === generation) loading.set(false);
    }
  }

  return {
    options: options.asReadonly(),
    loading: loading.asReadonly(),
    hasMore: hasMore.asReadonly(),
    onSearch,
    onLoadMore,
  };
}

export type AsyncSearchList<T extends { id: string }> = ReturnType<typeof useAsyncSearchList<T>>;
